// src/pages/Reports.jsx - Redesigned with Modern Orange-to-Pink Theme
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import LeApiService from '../services/leApiService';

const Reports = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [isLoadingCourts, setIsLoadingCourts] = useState(true);
  const [courtsError, setCourtsError] = useState('');
  const [isLoadingJudgements, setIsLoadingJudgements] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState(null);
  const [searchText, setSearchText] = useState("");
  
  const stateCodeMap = [
    { key: "Law Commission", value: "LCRF" },
    { key: "Constituent Assembly Debates", value: "DAC" },
  ];

  useEffect(() => {
    loadCourts();
  }, []);

  // Load courts from API
  const loadCourts = async () => {
    try {
      setIsLoadingCourts(true);
      setSelectedCourt('LCRF');
      loadJudgements('LCRF');
    } catch (error) {
      console.error('❌ Error loading reports:', error);
      setCourtsError(error.message || 'Failed to load reports');
    } finally {
      setIsLoadingCourts(false);
    }
  };

  // Load reports for selected type
  const loadJudgements = async (courtKey) => {
    setIsLoadingJudgements(true);
    try {
      const apiResponse = await LeApiService.getReportList(courtKey);
      setBlogPosts(apiResponse || []);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setIsLoadingJudgements(false);
    }
  };

  const handleCourtSelect = (courtKey) => {
    setSelectedCourt(courtKey);
    loadJudgements(courtKey);
    setSearchText('');
  };

  const handleOpenLaw = (post) => {
    const url = `https://legaleagleweb.com/${selectedCourt}/${post}`;
    setTitle(post);
    setPdfUrl(url);
    setShowModal(true);
  };

  const filteredItems = blogPosts.filter(item =>
    item.actname.toLowerCase().includes(searchText.toLowerCase())
  );

  const getReportTypeName = () => {
    const found = stateCodeMap.find(item => item.value === selectedCourt);
    return found ? found.key : 'Reports';
  };

  return (
    <div className="reports-layout">
      <Navbar />
      <SearchNavigation />

      <div className="reports-main-content">
        {/* Hero Section */}
        <div className="reports-hero">
          <div className="hero-badge">
            <i className="bx bx-file-find"></i>
            <span>Legal Reports</span>
          </div>
          <h1 className="hero-title">
            Official Reports & Documents
          </h1>
          <p className="hero-subtitle">
            Access Law Commission reports and Constituent Assembly debates
          </p>
        </div>

        <div className="reports-container">
          <div className="reports-grid">
            {/* Left Sidebar - Report Types */}
            <div className="reports-sidebar">
              <div className="sidebar-header">
                <div className="header-icon">
                  <i className="bx bx-folder-open"></i>
                </div>
                <div className="header-content">
                  <h3 className="sidebar-title">Report Categories</h3>
                  <p className="sidebar-subtitle">{stateCodeMap.length} categor{stateCodeMap.length !== 1 ? 'ies' : 'y'}</p>
                </div>
              </div>

              <div className="sidebar-body">
                {isLoadingCourts ? (
                  <div className="loading-state">
                    <i className="bx bx-loader-alt bx-spin"></i>
                    <p>Loading categories...</p>
                  </div>
                ) : courtsError ? (
                  <div className="error-state">
                    <i className="bx bx-error-circle"></i>
                    <p>{courtsError}</p>
                  </div>
                ) : (
                  <div className="categories-list">
                    {stateCodeMap.map((state, idx) => (
                      <div
                        key={idx}
                        className={`category-item ${selectedCourt === state.value ? 'active' : ''}`}
                        onClick={() => handleCourtSelect(state.value)}
                      >
                        <div className="category-icon">
                          <i className="bx bx-folder"></i>
                        </div>
                        <div className="category-info">
                          <span className="category-name">{state.key}</span>
                        </div>
                        <div className="category-arrow">
                          <i className="bx bx-chevron-right"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Content - Reports List */}
            <div className="reports-content">
              <div className="content-header">
                <div className="header-info">
                  <div className="report-badge">
                    <i className="bx bx-category"></i>
                    <span>Category</span>
                  </div>
                  <h3 className="content-title">{getReportTypeName()}</h3>
                  <p className="content-subtitle">
                    {filteredItems.length} report{filteredItems.length !== 1 ? 's' : ''} available
                  </p>
                </div>

                {/* Search Box */}
                <div className="search-box-header">
                  <i className="bx bx-search search-icon"></i>
                  <input
                    type="text"
                    className="search-input"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search reports..."
                  />
                  {searchText && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchText('')}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  )}
                </div>
              </div>

              <div className="content-body">
                {isLoadingJudgements ? (
                  <div className="loading-reports">
                    <i className="bx bx-loader-alt bx-spin"></i>
                    <p>Loading reports...</p>
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="empty-reports">
                    <i className="bx bx-folder-open"></i>
                    <p>{searchText ? `No reports found matching "${searchText}"` : 'No reports available'}</p>
                  </div>
                ) : (
                  <div className="reports-list">
                    {filteredItems.map((post, index) => (
                      <div key={index} className="report-card">
                        <div className="report-card-inner">
                          <div className="report-icon">
                            <i className="bx bx-file-blank"></i>
                          </div>
                          <div className="report-content">
                            <h4 
                              className="report-title"
                              onClick={(e) => {
                                e.preventDefault();
                                handleOpenLaw(post.actlink);
                              }}
                            >
                              {post.actname}
                            </h4>
                          </div>
                          <div className="report-action">
                            <button
                              className="view-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                handleOpenLaw(post.actlink);
                              }}
                            >
                              <i className="bx bx-show"></i>
                              <span>View</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <i className="bx bx-file-blank"></i>
                <span>{title || 'Report Viewer'}</span>
              </div>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>
            <div className="modal-body">
              <iframe
                src={pdfUrl}
                className="modal-iframe"
                title="Report Viewer"
              />
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* ============================================
           BASE LAYOUT
        ============================================ */
        .reports-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .reports-main-content {
          padding-top: 140px;
          width: 100%;
        }

        /* ============================================
           HERO SECTION
        ============================================ */
        .reports-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 50px 20px 40px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .hero-badge i {
          font-size: 16px;
        }

        .hero-title {
          font-size: 2rem;
          font-weight: 800;
          margin: 0 0 10px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1rem;
          opacity: 0.95;
          margin: 0;
          font-weight: 400;
        }

        /* ============================================
           REPORTS CONTAINER
        ============================================ */
        .reports-container {
          max-width: 1400px;
          margin: -30px auto 0;
          padding: 0 20px 40px;
          position: relative;
          z-index: 10;
        }

        .reports-grid {
          display: grid;
          grid-template-columns: 320px 1fr;
          gap: 20px;
          min-height: 600px;
        }

        /* ============================================
           LEFT SIDEBAR
        ============================================ */
        .reports-sidebar {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          height: fit-content;
          position: sticky;
          top: 160px;
        }

        .sidebar-header {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 14px;
          border-bottom: 2px solid #f5f5f5;
        }

        .header-icon {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 22px;
          flex-shrink: 0;
        }

        .header-content {
          flex: 1;
        }

        .sidebar-title {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          margin: 0 0 3px 0;
        }

        .sidebar-subtitle {
          font-size: 12px;
          color: #666;
          margin: 0;
        }

        .sidebar-body {
          max-height: 400px;
          overflow-y: auto;
        }

        /* States */
        .loading-state,
        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          gap: 12px;
        }

        .loading-state i,
        .error-state i {
          font-size: 40px;
          color: #E91E63;
        }

        .error-state i {
          color: #c41849;
        }

        .loading-state p,
        .error-state p {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        /* Categories List */
        .categories-list {
          padding: 8px;
        }

        .category-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          margin-bottom: 6px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .category-item:hover {
          background: #f8f9fa;
          transform: translateX(4px);
        }

        .category-item.active {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          border-color: #c41849;
          box-shadow: 0 2px 10px rgba(233, 30, 99, 0.15);
        }

        .category-icon {
          width: 38px;
          height: 38px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.15), rgba(233, 30, 99, 0.1));
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #E91E63;
          font-size: 18px;
          flex-shrink: 0;
        }

        .category-item.active .category-icon {
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
        }

        .category-info {
          flex: 1;
        }

        .category-name {
          font-size: 15px;
          font-weight: 600;
          color: #333;
        }

        .category-item.active .category-name {
          color: #c41849;
        }

        .category-arrow {
          color: #ccc;
          font-size: 18px;
          transition: all 0.3s ease;
        }

        .category-item:hover .category-arrow,
        .category-item.active .category-arrow {
          color: #c41849;
          transform: translateX(3px);
        }

        /* Scrollbar */
        .sidebar-body::-webkit-scrollbar {
          width: 5px;
        }

        .sidebar-body::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 4px;
        }

        /* ============================================
           RIGHT CONTENT
        ============================================ */
        .reports-content {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          display: flex;
          flex-direction: column;
        }

        .content-header {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          padding: 24px 28px;
          border-bottom: 2px solid #f5f5f5;
        }

        .header-info {
          margin-bottom: 16px;
        }

        .report-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.15), rgba(233, 30, 99, 0.1));
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          color: #E91E63;
          margin-bottom: 10px;
        }

        .report-badge i {
          font-size: 14px;
        }

        .content-title {
          font-size: 22px;
          font-weight: 700;
          color: #333;
          margin: 0 0 5px 0;
        }

        .content-subtitle {
          font-size: 13px;
          color: #666;
          margin: 0;
        }

        /* Search Box in Header */
        .search-box-header {
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          font-size: 18px;
        }

        .search-input {
          width: 100%;
          padding: 12px 40px 12px 44px;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          font-size: 14px;
          color: #333;
          background: white;
          transition: all 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #E91E63;
          box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.1);
        }

        .clear-search {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 24px;
          height: 24px;
          border: none;
          background: #f0f0f0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #666;
          transition: all 0.2s ease;
        }

        .clear-search:hover {
          background: #e0e0e0;
          color: #333;
        }

        .content-body {
          flex: 1;
          overflow: auto;
          padding: 20px;
        }

        /* Report States */
        .loading-reports,
        .empty-reports {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 15px;
        }

        .loading-reports i,
        .empty-reports i {
          font-size: 60px;
          color: #E91E63;
        }

        .empty-reports i {
          color: #ddd;
        }

        .loading-reports p,
        .empty-reports p {
          font-size: 16px;
          color: #666;
          margin: 0;
          text-align: center;
        }

        /* ============================================
           REPORTS LIST
        ============================================ */
        .reports-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .report-card {
          background: white;
          border: 2px solid #f0f0f0;
          border-radius: 12px;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .report-card:hover {
          border-color: #E91E63;
          box-shadow: 0 4px 16px rgba(245, 166, 35, 0.15);
          transform: translateY(-2px);
        }

        .report-card-inner {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 18px 20px;
        }

        .report-icon {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.15), rgba(233, 30, 99, 0.1));
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #E91E63;
          font-size: 22px;
          flex-shrink: 0;
        }

        .report-content {
          flex: 1;
          min-width: 0;
        }

        .report-title {
          font-size: 15px;
          font-weight: 600;
          color: #333;
          margin: 0;
          cursor: pointer;
          transition: all 0.2s ease;
          line-height: 1.4;
        }

        .report-title:hover {
          color: #E91E63;
        }

        .report-action {
          flex-shrink: 0;
        }

        .view-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .view-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(233, 30, 99, 0.3);
        }

        .view-btn i {
          font-size: 18px;
        }

        /* ============================================
           MODAL
        ============================================ */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .modal-container {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 1200px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 2px solid #f0f0f0;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
        }

        .modal-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 18px;
          font-weight: 700;
          color: #333;
        }

        .modal-title i {
          font-size: 24px;
          color: #E91E63;
        }

        .modal-close {
          width: 36px;
          height: 36px;
          border: none;
          background: #f0f0f0;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #666;
          transition: all 0.2s ease;
        }

        .modal-close:hover {
          background: #e0e0e0;
          color: #333;
        }

        .modal-close i {
          font-size: 24px;
        }

        .modal-body {
          flex: 1;
          overflow: hidden;
          padding: 0;
        }

        .modal-iframe {
          width: 100%;
          height: calc(90vh - 80px);
          border: none;
        }

        /* ============================================
           ANIMATIONS
        ============================================ */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .bx-spin {
          animation: spin 1s linear infinite;
        }

        /* ============================================
           RESPONSIVE DESIGN
        ============================================ */
        @media (max-width: 1024px) {
          .reports-grid {
            grid-template-columns: 280px 1fr;
            gap: 16px;
          }

          .reports-sidebar {
            top: 150px;
          }
        }

        @media (max-width: 768px) {
          .reports-main-content {
            padding-top: 130px;
          }

          .reports-hero {
            padding: 35px 15px 30px;
          }

          .hero-title {
            font-size: 1.6rem;
          }

          .hero-subtitle {
            font-size: 0.95rem;
          }

          .reports-container {
            margin-top: -20px;
            padding: 0 15px 30px;
          }

          .reports-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .reports-sidebar {
            position: relative;
            top: 0;
          }

          .sidebar-body {
            max-height: 300px;
          }

          .content-header {
            padding: 20px 22px;
          }

          .content-title {
            font-size: 18px;
          }

          .report-card-inner {
            padding: 14px 16px;
          }

          .view-btn span {
            display: none;
          }

          .view-btn {
            padding: 10px 14px;
          }

          .modal-container {
            max-height: 95vh;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.4rem;
          }

          .hero-badge {
            padding: 6px 14px;
            font-size: 12px;
          }

          .sidebar-header {
            padding: 16px;
          }

          .header-icon {
            width: 38px;
            height: 38px;
            font-size: 18px;
          }

          .sidebar-title {
            font-size: 14px;
          }

          .content-header {
            padding: 18px 20px;
          }

          .content-title {
            font-size: 16px;
          }

          .report-icon {
            width: 38px;
            height: 38px;
            font-size: 18px;
          }

          .report-title {
            font-size: 14px;
          }
        }
              /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Reports;